import { apiClient } from './client';

export interface WallPost {
  id: string;
  authorId: string;
  ownerId: string;
  content: string;
  color: string;
  mediaFileId: string | null;
  pinnedAt: string | null;
  expiresAt: string | null;
  remindAt: string | null;
  createdAt: string;
  author: { id: string; username: string; displayName: string; avatarUrl: string | null };
  _count: { likes: number; comments: number };
  likedByMe: boolean;
}

export interface WallComment {
  id: string;
  postId: string;
  authorId: string;
  content: string;
  createdAt: string;
  author: { id: string; username: string; displayName: string; avatarUrl: string | null };
}

export interface ProfilePhoto {
  id: string;
  userId: string;
  mediaFileId: string;
  caption: string | null;
  position: number;
  createdAt: string;
  media: { id: string; thumbnailPath: string | null; mimeType: string; size: number };
}

export interface FullProfile {
  user: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
    bio: string | null;
    status: string;
    globalRole: string;
    wallPrivacy: string;
    createdAt: string;
    lastSeenAt: string | null;
    karma?: { score: number; rank: number | null };
  };
  stats: { friends: number; photos: number; posts: number };
  recentPhotos: ProfilePhoto[];
  recentPosts: WallPost[];
  stories: Array<{ id: string; mediaFile: { id: string; thumbnailPath: string | null } }>;
  friendship: { status: string; friendshipId: string; isSender: boolean } | null;
}

export const wallApi = {
  /** identifier = @username or cuid */
  async getFullProfile(identifier: string): Promise<FullProfile> {
    const { data } = await apiClient.get(`/api/wall/profile/${encodeURIComponent(identifier)}`);
    return data;
  },

  async listPosts(identifier: string, cursor?: string): Promise<{ data: WallPost[]; nextCursor: string | null }> {
    const { data } = await apiClient.get(`/api/wall/${encodeURIComponent(identifier)}`, { params: { cursor, limit: 20 } });
    return data;
  },

  async createPost(
    identifier: string,
    content: string,
    color?: string,
    mediaFileId?: string,
    opts?: { expiresAt?: string | null; remindAt?: string | null },
  ): Promise<WallPost> {
    const { data } = await apiClient.post(`/api/wall/${encodeURIComponent(identifier)}`, {
      content,
      color,
      mediaFileId,
      expiresAt: opts?.expiresAt ?? undefined,
      remindAt: opts?.remindAt ?? undefined,
    });
    return data;
  },

  async deletePost(postId: string): Promise<void> {
    await apiClient.delete(`/api/wall/posts/${postId}`);
  },

  async likePost(postId: string): Promise<void> {
    await apiClient.post(`/api/wall/posts/${postId}/like`);
  },

  async unlikePost(postId: string): Promise<void> {
    await apiClient.delete(`/api/wall/posts/${postId}/like`);
  },

  async listComments(postId: string, cursor?: string): Promise<{ data: WallComment[]; nextCursor: string | null }> {
    const { data } = await apiClient.get(`/api/wall/posts/${postId}/comments`, { params: { cursor } });
    return data;
  },

  async addComment(postId: string, content: string): Promise<WallComment> {
    const { data } = await apiClient.post(`/api/wall/posts/${postId}/comments`, { content });
    return data;
  },

  async deleteComment(commentId: string): Promise<void> {
    await apiClient.delete(`/api/wall/comments/${commentId}`);
  },

  async listPhotos(userId: string, cursor?: string): Promise<{ data: ProfilePhoto[]; nextCursor: string | null }> {
    const { data } = await apiClient.get(`/api/wall/photos/${userId}`, { params: { cursor } });
    return data;
  },

  async uploadPhoto(mediaFileId: string, caption?: string): Promise<ProfilePhoto> {
    const { data } = await apiClient.post('/api/wall/photos', { mediaFileId, caption });
    return data;
  },

  async deletePhoto(photoId: string): Promise<void> {
    await apiClient.delete(`/api/wall/photos/${photoId}`);
  },
};
